/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { type ReactElement, type ReactNode } from 'react';
import { Box, Typography, type TypographyKind } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_NEU_STL, CF_SEC_BLD, testIds } from '../../constants';

import './SodFieldSet.css';

/**
 * Props interface for the SodFieldSet component
 */
interface SodFieldSetProps {
  label: string;
  value: string | ReactElement | ReactNode;
  stringColor?: string;
  className?: string;
  secondaryTextStyle?: TypographyKind;
  isNotProvided?: boolean;
}

/**
 * SodFieldSet Component
 *
 * A component that displays a label-value pair in a consistent format.
 * The label is displayed above the value with specific styling.
 * If the value is a string, it's rendered with Typography component.
 * For non-string values, they're wrapped in a Box component.
 *
 * @component
 * @example
 * // With string value
 * <SodFieldSet
 *   label="Status"
 *   value="Active"
 * />
 *
 * @example
 * // With React element value
 * <SodFieldSet
 *   label="Actions"
 *   value={<Button>Click me</Button>}
 * />
 */
function SodFieldSet({
  label = '',
  value = '',
  stringColor,
  className,
  secondaryTextStyle = 'body2',
  isNotProvided,
}: SodFieldSetProps): ReactElement {
  const isStringvalue = Boolean(typeof value === 'string');

  const sodFieldSetClasses = cx('SodFieldSet', className);

  const getStringColor = () => {
    if (isNotProvided) {
      return CF_NEU_STL;
    }

    if (stringColor) {
      return stringColor;
    }

    return CF_SEC_BLD;
  };

  return (
    <Box
      tag='div'
      className={sodFieldSetClasses}
      dataTestId={testIds.SOD_FIELDSET}>
      <Typography
        kind='body3'
        desktop='body3-d'
        mobile='body3-d'
        lineLimit={1}
        color={CF_NEU_STL}>
        {label}
      </Typography>
      {isStringvalue ? (
        <Typography
          kind={secondaryTextStyle}
          lineLimit={3}
          color={getStringColor()}>
          {value as string}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
}

/**
 * Default props for the SodFooter component
 */
SodFieldSet.defaultProps = {
  stringColor: null,
  className: '',
  secondaryTextStyle: 'body2',
  isNotProvided: false,
};

export default SodFieldSet;
